import { Ajax } from '@shared/Ajax';
import { Drawer } from '@shared/ui-native/Drawer';
import { Notify } from '@shared/ui-native/Notify';
import { CollapsiblePanel } from '@shared/ui-react/CollapsiblePanel';
import { FiletreeSelect } from '@shared/ui-react/FiletreeSelect';
import { FiletreeSelectReact } from '@shared/ui-react/FiletreeSelectReact';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { autorun, makeObservable, observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import Feature from 'ol/Feature';
import VectorSource from 'ol/source/Vector';
import * as React from 'react';
import { mapRootStore, mapUI, mapWrapper } from '../../map';
import './MassGridEditor.scss';


/**
 * @author NikolaV
 */

declare var RotateFeatureInteraction: any;

export const MassAdvancedEditor = {
    open(onSaved?: (s: any) => void, folderId?: number) {
        const el = Drawer.open();
        ReactUtils.safeRender(<AdvancedEditorForm onSaved={onSaved} folderId={folderId} />, el);
    },
    edit(sheet: Mass.SheetAdvanced, onSaved?: (s: any) => void) {
        const el = Drawer.open();

        ReactUtils.safeRender(<AdvancedEditorForm
            id={sheet.id}
            name={sheet.name}
            folderId={sheet.folderId}
            stationStart={sheet.stationStart}
            stationEnd={sheet.stationEnd}
            stationInterval={sheet.stationInterval}
            heightOffset={sheet.heightOffset}
            leftLimit={sheet.leftLimit}
            rightLimit={sheet.rightLimit}
            alignmentId={sheet.alignmentModel ? sheet.alignmentModel.id : null}
            calculationLayerId={sheet.model1Id}
            referenceLayerId={sheet.model2Id}
            limitLayerId={sheet.model3Id}
            onSaved={onSaved}
        />, el);
    },
    close() {
        const el = Drawer.close();
        ReactUtils.safeUnmountComponentAtNode(el);
    },
};

interface IProps {
    id?: number;
    folderId?: number;
    name?: string;

    stationStart?: number;
    stationEnd?: number;
    stationInterval?: number;

    heightOffset?: number;
    leftLimit?: number;
    rightLimit?: number;

    alignmentId?: number;
    calculationLayerId?: number;
    referenceLayerId?: number;
    limitLayerId?: number;

    onSaved?: (s: any) => void;
}

@observer
class AdvancedEditorForm extends React.Component<IProps> {
    static defaultProps: IProps = {
        name: kuura.getMessage('mass.task_type.advanced') + " " + Date.now(),

        folderId: -1,
        stationStart: 0,
        stationEnd: 0,
        stationInterval: 20,

        heightOffset: 0,
        leftLimit: 0,
        rightLimit: 0,

        alignmentId: -1,
        calculationLayerId: -1,
        referenceLayerId: -1,
        limitLayerId: -1,
    };

    @observable formState: IProps = {
        ...(this.props as IProps),
        folderId: this.props.folderId || mapRootStore.treeData.id
    };

    @observable editable = true;

    constructor(props: IProps) {
        super(props);
        makeObservable(this);
    }

    get isEditMode(): boolean {
        return !!this.props.id || !!this.formState.id;
    }

    private modelsSource: VectorSource;
    private models: Feature[] = [];

    componentDidMount() {
        //this.gridFeature = new MassGridLayer(mapInstance);

        mapWrapper.featureLayers.models.layerSource.on('addfeature', () => {
            this.setModels();
            this.draw();
        });
        mapWrapper.featureLayers.models.layerSource.on('removefeature', () => {
            this.setModels();
            this.draw();
        });
        reaction(() => this.formState, () => this.draw());

        autorun(this.loadModels, { delay: 1 });
    }

    componentWillUnmount() {
        //this.gridFeature.destroy();
        mapWrapper.featureLayers.models.layerSource.un('addfeature', this.draw);
        mapWrapper.featureLayers.models.layerSource.un('removefeature', this.draw);
    }

    draw = () => {
        // todo
        //this.cells = this.gridFeature.draw(this.size, this.rotation, this.modelsSource);
    };

    submit = () => {
        const data: any = {
            taskId: this.props.id,
            taskType: 1,
            taskName: this.formState.name,
            sheetVisibility: 1,
            sheetLocked: false,
            sheetAlignment: this.formState.alignmentId,
            stationStart: this.formState.stationStart,
            stationEnd: this.formState.stationEnd,
            stationInterval: this.formState.stationInterval,
            surfaceTopId: this.formState.calculationLayerId,
            surfaceBottomId: this.formState.referenceLayerId,
            surfaceLimitId: this.formState.limitLayerId,
            bottomSurfaceOffset: this.formState.heightOffset,
            sideLimitLeft: this.formState.leftLimit,
            sideLimitRight: this.formState.rightLimit,
            folderId: this.formState.folderId,
        };

        Ajax({
            url: 'api/1/mass/sheet.json',
            method: 'post',
            data,
        }).done(resp => {
            if (!data.taskId) {
                data.taskId = resp.sheet.id;

                Ajax({
                    url: 'api/1/mass/sheet.json',
                    method: 'post',
                    data,
                }).done(() => {
                    this.editable = false;
                    Notify.success();

                    if (typeof this.props.onSaved === 'function') {
                        this.props.onSaved(resp);
                    }
                }).fail(Notify.error);
            } else {
                Notify.success();

                if (typeof this.props.onSaved === 'function') {
                    this.props.onSaved(resp);
                }
            }
        }).fail(Notify.error);
    };

    loadModels = () => {
        console.log('load models');
        mapUI.mapPanels.panels.models.clearSelection();
        mapUI.mapPanels.panels.models.checkIds([
            this.formState.alignmentId,
            this.formState.calculationLayerId,
            this.formState.referenceLayerId,
            this.formState.limitLayerId,
        ]);
    };

    setModels = (): void => {
        const orig = [
            mapWrapper.featureLayers.models.getFeaturesBySource({ modelId: this.formState.alignmentId })[0],
            mapWrapper.featureLayers.models.getFeaturesBySource({ modelId: this.formState.calculationLayerId })[0],
            mapWrapper.featureLayers.models.getFeaturesBySource({ modelId: this.formState.limitLayerId })[0],
            mapWrapper.featureLayers.models.getFeaturesBySource({ modelId: this.formState.referenceLayerId })[0],
        ];

        this.models = orig.map(f => f ? f.clone() : f);
        this.modelsSource = new VectorSource({ features: this.models.filter(Boolean) });

        // todo figure out why 'removefeature' fires
        // fitFeatures(mapInstance, this.models);
    };

    filetreeFilterModels(item) {
        if (item.tolerance) return true; // folder
        return kuuraModel.isModel(item);
    }

    filetreeFilterAlignments(item) {
        if (item.tolerance) return true; // folder
        return kuuraModel.isAlignment(item);
    }

    render() {
        return (
            <div style={{ height: '100%', overflow: 'auto' }}>
                <header>
                    <div className='padded'>
                        <h1 style={{ margin: 0 }}>{__('mass.advanced_task')}</h1>
                    </div>
                </header>

                <div className={!this.editable ? 'fade-disable' : null}>
                    <div className='form-group'>
                        <label className='styled'>{__('general.name')}</label>
                        <input type='text' value={this.formState.name} onChange={e => this.formState.name = e.currentTarget.value} />

                        <label className='styled'>{__('general.folder')}</label>
                        <FiletreeSelect name='folderId'
                            filetreeData={mapRootStore.treeData} includeRoot={true} firstEmpty={false}
                            value={this.formState.folderId} onChange={id => this.formState.folderId = id} />
                    </div>

                    <div className='form-group'>
                        <label className='styled'>{__('mass.station')}</label>
                        <div className='flex-row'>
                            <div>
                                <label className='styled'>{__('mass.station_start')}</label>
                                <input type='number' min='0' step='1' value={this.formState.stationStart}
                                    onChange={e => this.formState.stationStart = utils.clamp(0, e.currentTarget.value as any, 99999999)} />
                            </div>

                            <div style={{ margin: '0 1em' }}>
                                <label className='styled'>{__('mass.station_end')}</label>
                                <input type='number' min='0' step='1' value={this.formState.stationEnd}
                                    onChange={e => this.formState.stationEnd = utils.clamp(0, e.currentTarget.value as any, 99999999)} />
                            </div>

                            <div>
                                <label className='styled'>{__('mass.station_interval')}</label>
                                <input type='number' min='0' step='1' value={this.formState.stationInterval}
                                    onChange={e => this.formState.stationInterval = utils.clamp(0, e.currentTarget.value as any, 99999999)} />
                            </div>
                        </div>
                    </div>

                    <div className='form-group'>
                        <label className='styled'>{__('mass.alignment')}</label>
                        <div className='flex-row'>
                            <FiletreeSelectReact selectable='file' formFieldName='alignment' includeRoot={true} canClearValue={true} hideEmptyFolders={true}
                                treeData={mapRootStore.treeData} childFilter={this.filetreeFilterAlignments} nullValue={'-1'}
                                onChange={id => this.formState.alignmentId = id ? parseInt(id) : -1} selectedId={this.formState.alignmentId} />
                        </div>
                        <br />
                        <label className='styled'>{__('mass.top_surface')}</label>
                        <div className='flex-row'>
                            <FiletreeSelectReact selectable='file' formFieldName='calculationLayer' includeRoot={true} canClearValue={true} hideEmptyFolders={true}
                                treeData={mapRootStore.treeData} childFilter={this.filetreeFilterModels} nullValue={'-1'}
                                onChange={id => this.formState.calculationLayerId = id ? parseInt(id) : -1} selectedId={this.formState.calculationLayerId} />
                        </div>
                        <br />
                        <label className='styled'>{__('mass.bottom_surface')}</label>
                        <div className='flex-row'>
                            <FiletreeSelectReact selectable='file' formFieldName='referenceLayer' includeRoot={true} canClearValue={true} hideEmptyFolders={true}
                                treeData={mapRootStore.treeData} childFilter={this.filetreeFilterModels} nullValue={'-1'}
                                onChange={id => this.formState.referenceLayerId = id ? parseInt(id) : -1} selectedId={this.formState.referenceLayerId} />
                        </div>
                    </div>

                    <div className='form-group'>
                        <CollapsiblePanel label={__('mass.optional_parameters')} defaultIsOpen={true}>
                            <div>
                                <br />
                                <label className='styled'>{__('mass.limit_surface')}</label>
                                <div className='flex-row'>
                                    <FiletreeSelectReact selectable='file' formFieldName='limitLayer' includeRoot={true} canClearValue={true} hideEmptyFolders={true}
                                        treeData={mapRootStore.treeData} childFilter={this.filetreeFilterModels} nullValue={'-1'}
                                        onChange={id => this.formState.limitLayerId = id ? parseInt(id) : -1} selectedId={this.formState.limitLayerId} />
                                </div>
                                <br />
                                <div className='flex-row'>
                                    <div>
                                        <label className='styled'>{__('mass.limit_left_side')}</label>
                                        <input type='number' min='0' step='1' value={this.formState.leftLimit}
                                            onChange={e => this.formState.leftLimit = utils.clamp(0, e.currentTarget.value as any, 99999999)} />
                                    </div>

                                    <div style={{ marginLeft: '1em' }}>
                                        <label className='styled'>{__('mass.limit_right_side')}</label>
                                        <input type='number' min='0' step='1' value={this.formState.rightLimit}
                                            onChange={e => this.formState.rightLimit = utils.clamp(0, e.currentTarget.value as any, 99999999)} />
                                    </div>
                                </div>
                                <br />
                                <div>
                                    <label className='styled'>{__('mass.offset')}</label>
                                    <input type='number' min='0' step='1' value={this.formState.heightOffset}
                                        onChange={e => this.formState.heightOffset = utils.clamp(0, e.currentTarget.value as any, 99999999)} />
                                </div>
                            </div>
                        </CollapsiblePanel>
                    </div>
                </div>

                <footer style={{ display: 'flex' }}>
                    <button onClick={MassAdvancedEditor.close} className='ik-button-secondary--subtle'><i className='iki iki-chevron-left'></i> {__('general.close')}</button>
                    <button onClick={this.submit} disabled={!this.editable} style={{ marginLeft: 'auto' }} className='ik-button-primary'>{__(this.isEditMode ? 'general.save' : 'general.add')}</button>
                </footer>
            </div>
        );
    }
}
